import { format } from 'date-fns';
import { getUrlWithoutDomain } from '@/helpers/getUrlWithoutDomain';
import type { Comedian_Entry, LanguageCategory, ShowCategory } from '@/types/custom';
import type { Event_Entry } from '@/types/graphql';
import type { EventWithImages } from '@/types/query-types';

export function formatShowDate(date: string | null | undefined): string {
    if (!date) return '';
    return format(new Date(date), 'EEEE d MMMM yyyy');
}

export function getShowStatus(event: Event_Entry): string {
    if (event.eventTicketLinkOverride) return 'active';
    if (event.eventTicketSaleStart && Date.parse(event.eventTicketSaleStart) > Date.now())
        return 'upcoming';
    return event.eventStatus || '';
}

export function getShowPrimaryLanguage(event: Event_Entry): LanguageCategory | undefined {
    const languages = event.eventLanguage?.filter(Boolean) || [];
    return languages[0] as LanguageCategory | undefined;
}

export function getShowTitle(event: Event_Entry): string {
    return event.eventTitle?.replace(/\([0-9][0-9]:[0-9][0-9]\)/g, '') || '';
}

export function getShowSubtitle(event: Event_Entry): string {
    return event.eventSubtitle || '';
}

export function getShowDescription(event: Event_Entry): string {
    return event.eventDescription?.replace(/(?:\r\n|\r|\n)/g, '  \n') || '';
}

export function getShowHeaderImage(event: EventWithImages) {
    return event.eventDesktopHeaderImage?.[0] || event.eventDesktopHeaderImageFallback?.[0] || null;
}

export function getShowArtists(event: Event_Entry) {
    return (event.artists || []) as Comedian_Entry[];
}

export function getShowCategories(event: Event_Entry) {
    return (event.eventCategories || []) as ShowCategory[];
}

export function getShowAlternativeLanguages(event: Event_Entry) {
    return event.localized || [];
}

type TicketPriceRow = NonNullable<NonNullable<Event_Entry['eventTicketPrices']>[number]>;

/**
 * Returns true when a ticket row is sold out.
 *
 * Note: the GraphQL schema types `ticketSoldOut` as `String`, but the Stager
 * importer stores a boolean. We therefore accept both shapes defensively.
 */
export function isTicketSoldOut(ticket: TicketPriceRow): boolean {
    const raw = ticket.ticketSoldOut as unknown;
    if (raw === true) return true;
    if (typeof raw === 'string') {
        return raw.toLowerCase() === 'true' || raw === '1';
    }
    return false;
}

/**
 * Picks the "from" ticket row used to render the price on the show page.
 *
 * Logic (CCHG-144): walk `eventTicketPrices` in Stager order and return the
 * first non sold-out ticket. If every ticket is sold out, fall back to the
 * first row so the price indicator stays populated.
 */
export function getShowFromTicket(event: Event_Entry): TicketPriceRow | null {
    const prices = (event.eventTicketPrices || []).filter(
        (ticket): ticket is TicketPriceRow => ticket != null,
    );

    if (prices.length === 0) {
        return null;
    }

    return prices.find((ticket) => !isTicketSoldOut(ticket)) ?? prices[0];
}

export function getShowTicketPrice(event: Event_Entry): number | null {
    return getShowFromTicket(event)?.ticketPrice || null;
}

export function getShowTicketName(event: Event_Entry): string | null {
    return getShowFromTicket(event)?.ticketName || null;
}

export function formatShowPrice(price: number | null): string {
    return price ? `€${price.toFixed(2)}` : '';
}

export function getShowProgramStart(event: Event_Entry): string {
    return event.eventProgramStart || '';
}

export function getShowProgramEnd(event: Event_Entry): string {
    return event.eventProgramEnd || '';
}

export function getShowPrices(event: Event_Entry) {
    return event.eventTicketPrices || [];
}

export function getShowPageLink(event: Event_Entry): string {
    return getUrlWithoutDomain(event.url || '') || '';
}

export function getShowTicketLink(event: Event_Entry): string {
    return event.eventTicketLinkOverride || event.eventTicketLink || '';
}

export function getShowImage(event: EventWithImages) {
    return event.eventDesktopHeaderImage?.[0] || event.eventDesktopHeaderImageFallback?.[0];
}

export function getAdditionalShowImages(event: EventWithImages) {
    return event.eventMobileHeaderImage?.[0];
}

export function getShowLanguages(event: Event_Entry) {
    return (event.eventLanguage || []) as LanguageCategory[];
}

export function getShowVenue(event: Event_Entry): string {
    return event.venue?.[0]?.title || '';
}

export function getShowEventStatus(event: Event_Entry): string {
    if (event.eventTicketLinkOverride) {
        return 'active';
    }

    if (event.eventTicketSaleStart && Date.parse(event.eventTicketSaleStart) > Date.now()) {
        return 'upcoming';
    }

    return event.eventStatus || '';
}

export function getShowAlmostSoldOut(event: Event_Entry): boolean {
    return event.eventTotalTicketsRemaining < 0.11 * event.eventTotalTicketsAmount;
}

export function getShowButtonText(event: Event_Entry): string {
    const status = getShowEventStatus(event);

    if (status === 'upcoming') {
        return 'upcoming';
    }

    if (status === 'active' && !event.eventTicketLinkOverride && getShowAlmostSoldOut(event)) {
        return 'latest-tickets';
    }

    if (status === 'sold-out') {
        return 'sold-out';
    }

    if (status === 'cancelled') {
        return 'cancelled';
    }

    return 'buy-tickets';
}

export function getShouldDisplayPurchaseButton(event: Event_Entry): boolean {
    return (
        new Date(event.eventProgramStart || '') > new Date() &&
        (event.eventStatus === 'active' || event.eventStatus === 'upcoming')
    );
}

export function getShowButtonVariant(event: Event_Entry): string {
    const status = getShowEventStatus(event);

    if (status === 'upcoming') {
        return 'upcoming';
    }

    if (status === 'active' && !event.eventTicketLinkOverride && getShowAlmostSoldOut(event)) {
        return 'almost-sold-out';
    }

    if (status === 'sold-out') {
        return 'sold-out';
    }

    if (status === 'cancelled') {
        return 'cancelled';
    }

    return 'active';
}

export function getShowEventColor(event: Event_Entry): string {
    if (event.eventStatus === 'upcoming') {
        return 'upcoming';
    }

    if (
        event.eventStatus === 'active' &&
        !event.eventTicketLinkOverride &&
        getShowAlmostSoldOut(event)
    ) {
        return 'almost-sold-out';
    }

    if (event.eventStatus === 'sold-out') {
        return 'sold-out';
    }

    if (event.eventStatus === 'cancelled') {
        return 'cancelled';
    }

    return 'active';
}
